import React, { useCallback, useEffect, useState } from 'react';
import { api } from '../services/api';
import { PendingLocation } from '../types';
import AddressMap from './AddressMap';

interface Props {
  /** Javlja spolja da je jedan objekat potvrđen, da se radne liste osveže. */
  onConfirmed?: () => void;
}

/**
 * Objekti mreže kojima koordinata nije potvrđena.
 *
 * Ovo NIJE isto što i mesto na pravcu, iako se zovu isto. „Vrbas" na pravcu je
 * tačka kroz koju tura prolazi i služi za računanje pojasa; „PRODAJNO MESTO
 * VRBAS" je stanica na koju se roba istovara. Prvo se potvrđuje u kartici
 * Pravci, drugo ovde — i tek drugo pušta turu.
 *
 * Objekata je desetak i potvrđuju se jednom, pa ovaj spisak najčešće stoji
 * prazan. Postoji zato što se bez njega tura zaustavi porukom na koju operater
 * nije imao gde da odgovori.
 */
export default function LocationQueue({ onConfirmed }: Props) {
  const [pending, setPending] = useState<PendingLocation[]>([]);
  const [activeId, setActiveId] = useState<number | null>(null);
  const [point, setPoint] = useState<{ latitude: number; longitude: number } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const list = await api.pendingLocations();
      setPending(list);
      setActiveId((current) =>
        current !== null && list.some((l) => l.id === current) ? current : list[0]?.id ?? null);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const active = pending.find((l) => l.id === activeId) ?? null;

  // Predlog iz ERP-a je polazna tačka, a ne odgovor: operater ga pomera ako ne
  // stoji gde treba. Zato se pin drži odvojeno od učitanog podatka.
  useEffect(() => {
    setPoint(active && active.latitude !== null && active.longitude !== null
      ? { latitude: active.latitude, longitude: active.longitude }
      : null);
  }, [active]);

  const confirm = async () => {
    if (!active || !point) return;

    setBusy(true);
    try {
      await api.confirmLocation(active.id, point.latitude, point.longitude);
      await load();
      onConfirmed?.();
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  if (pending.length === 0) {
    return null;
  }

  return (
    <section className="panel panel--todo">
      <h2>
        Objekti bez potvrđene koordinate <span className="muted">· {pending.length}</span>
      </h2>

      <p className="note">
        Magacin ili prodavnica ne mogu ući u turu dok im se tačka ne potvrdi na
        mapi. Ovo nije isto što i mesto na pravcu — pravac služi za računanje
        pojasa, a ovo je tačka na koju se roba istovara.
      </p>

      {error && <div className="health health--bad">{error}</div>}

      <ul className="locqueue">
        {pending.map((location) => (
          <li key={location.id}>
            <button
              type="button"
              className={location.id === activeId ? 'locqueue__item locqueue__item--active' : 'locqueue__item'}
              onClick={() => setActiveId(location.id)}
            >
              {location.name}
              <span className="muted">
                {' '}· {location.kind === 'Warehouse' ? 'magacin' : 'prodavnica'} {location.code}
              </span>
              {location.address && <span className="locqueue__address">{location.address}</span>}
            </button>
          </li>
        ))}
      </ul>

      {active && (
        <>
          <p className="explanation">
            <strong>{active.name}</strong>
            {active.address ? ` — ${active.address}` : ''}
            {active.city ? `, ${active.city}` : ''}
            {point === null && ' · ERP nema koordinatu, klikni na mapu da je postaviš'}
          </p>

          <AddressMap
            latitude={point?.latitude ?? null}
            longitude={point?.longitude ?? null}
            tight
            onPick={(latitude, longitude) => setPoint({ latitude, longitude })}
          />

          <div className="actions">
            <button type="button" className="primary" disabled={busy || point === null} onClick={() => void confirm()}>
              Potvrdi tačku
            </button>
            {point && (
              <span className="muted">
                {point.latitude.toFixed(5)}, {point.longitude.toFixed(5)}
              </span>
            )}
          </div>
        </>
      )}
    </section>
  );
}
